/**
 * JWT Token Utilities
 *
 * Story 5.6: Admin OIDC Authentication
 *
 * Utility functions for JWT token handling on the frontend.
 */

/**
 * Decode JWT token payload without verification.
 *
 * This is safe to use on the frontend for extracting claims from tokens
 * that have already been validated by the OIDC library.
 *
 * @param token - JWT token string (format: header.payload.signature)
 * @returns Decoded payload as object, or null if token is invalid
 */
export function decodeJwtPayload(
  token: string
): Record<string, unknown> | null {
  try {
    // Split token into parts
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error(
        "[decodeJwtPayload] Invalid token format - expected 3 parts",
        {
          partsCount: parts.length,
        }
      );
      return null;
    }

    // Decode payload (second part)
    const payloadB64 = parts[1];

    // Handle base64 URL encoding (replace - with +, _ with /, add padding if needed)
    let base64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/");

    // Add padding if needed
    while (base64.length % 4) {
      base64 += "=";
    }

    // Decode base64
    const payloadJson = atob(base64);

    // Parse JSON
    const payload = JSON.parse(payloadJson);

    return payload;
  } catch (error) {
    console.error("[decodeJwtPayload] Error decoding token:", error);
    return null;
  }
}

/**
 * Extract sovra_user_id from JWT token.
 *
 * @param token - JWT token string
 * @returns sovra_user_id claim value, or null if not found or token is invalid
 */
export function extractSovraUserId(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  // Try sovra_user_id first, then fall back to sub
  const sovraUserId = payload.sovra_user_id || payload.sub;
  if (!sovraUserId || typeof sovraUserId !== "string") {
    console.warn("[extractSovraUserId] User ID claim not found in token", {
      availableClaims: Object.keys(payload),
    });
    return null;
  }

  return sovraUserId;
}

/**
 * Check if a token is a valid JWT format.
 *
 * @param token - Token string to validate
 * @returns true if token appears to be a valid JWT, false otherwise
 */
export function isValidJwtFormat(token: string): boolean {
  if (!token || typeof token !== "string") {
    return false;
  }

  const parts = token.split(".");
  // JWT has 3 parts, JWE (encrypted JWT) has 5 parts
  return parts.length === 3 || parts.length === 5;
}
